import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Search } from "lucide-react";
import { useRole } from "../context/RoleContext";
import {
  CATEGORIES,
  listNotifications,
  markAllRead,
  markRead,
  relativeTime,
} from "../lib/notifications";
import EmptyState from "../components/EmptyState";

const CATEGORY_TINT = {
  messages: "#1B1F4B",
  approvals: "#4F6149",
  status: "#3A5563",
  documents: "#B98A3C",
  warnings: "#A23B2E",
};

export default function Notifications() {
  const { user } = useRole();
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setData(null);
    const d = await listNotifications(user.id, { category, q: debouncedQ });
    setData(d);
  }, [user?.id, category, debouncedQ]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user?.id) {
    return (
      <section className="animate-fade-in-up" data-testid="notifications-anon">
        <EmptyState
          title="Sign in to see your notifications."
          body="Notifications are tied to your account."
          actionLabel="Go to sign in"
          onAction={() => navigate("/sign-in")}
        />
      </section>
    );
  }

  const onRowClick = async (n) => {
    if (!n.read) {
      try {
        await markRead(n.id, user.id);
      } catch {
        // navigate anyway
      }
    }
    if (n.route) navigate(n.route);
  };

  const onMarkAll = async () => {
    if (busy || !data || data.unread === 0) return;
    setBusy(true);
    try {
      await markAllRead(user.id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="animate-fade-in-up" data-testid="notifications-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
            <span className="wn-label" style={{ color: "#8F3C23" }}>Your activity</span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
          >
            Notifications.
          </h1>
          <p style={{ color: "#6C6B85", fontSize: 14, marginTop: 4 }}>
            Everything that's happened on your projects, consultations, and submissions.
          </p>
        </div>
        <button
          type="button"
          className="wn-btn wn-btn-tertiary"
          onClick={onMarkAll}
          disabled={!data || data.unread === 0 || busy}
          data-testid="notifications-mark-all-btn"
          style={{ opacity: data && data.unread > 0 ? 1 : 0.5 }}
        >
          <CheckCheck size={14} /> Mark all read
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="notifications-filters">
        {CATEGORIES.map((c) => {
          const active = category === c.v;
          return (
            <button
              key={c.v}
              onClick={() => setCategory(c.v)}
              data-testid={`notifications-filter-${c.v}`}
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                border: "1px solid " + (active ? "#1B1F4B" : "#D9D8E4"),
                background: active ? "#1B1F4B" : "transparent",
                color: active ? "#FBFAFD" : "#272636",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "background-color 140ms ease, color 140ms ease",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      <div
        className="mt-4"
        style={{ position: "relative", maxWidth: 480 }}
      >
        <Search
          size={15}
          color="#9AA0C0"
          style={{ position: "absolute", left: 12, top: 11 }}
          aria-hidden
        />
        <input
          className="wn-input"
          style={{ paddingLeft: 36 }}
          placeholder="Search notifications…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="notifications-search"
        />
      </div>

      <div className="mt-5">
        {data === null && (
          <div style={{ color: "#6C6B85", fontStyle: "italic" }}>Loading notifications…</div>
        )}
        {data && data.items.length === 0 && (
          <EmptyState
            title={q || category !== "all" ? "No notifications match." : "No notifications yet."}
            body={
              q || category !== "all"
                ? "Try widening your filter or clearing the search."
                : "We'll surface a row here the moment something changes."
            }
            actionLabel={q || category !== "all" ? "Clear filters" : null}
            onAction={() => {
              setQ("");
              setCategory("all");
            }}
          />
        )}
        {data && data.items.length > 0 && (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }} data-testid="notifications-list">
            {data.items.map((n) => (
              <li
                key={n.id}
                onClick={() => onRowClick(n)}
                role={n.route ? "button" : "listitem"}
                tabIndex={n.route ? 0 : -1}
                onKeyDown={(e) => {
                  if (n.route && (e.key === "Enter" || e.key === " ")) onRowClick(n);
                }}
                data-testid={`notifications-row-${n.id}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gap: 14,
                  padding: "16px 18px",
                  border: "1px solid #D9D8E4",
                  borderRadius: 6,
                  background: n.read ? "#FBFAFD" : "#F5F4FA",
                  marginBottom: 10,
                  cursor: n.route ? "pointer" : "default",
                  transition: "border-color 140ms ease",
                }}
              >
                <div style={{ paddingTop: 4 }}>
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: n.read ? "transparent" : "#B14E2F",
                      border: "1.5px solid " + (n.read ? "#D9D8E4" : "#B14E2F"),
                    }}
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    className="wn-label"
                    style={{
                      color: CATEGORY_TINT[n.category] || "#6C6B85",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {n.category}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: "#272636",
                      fontWeight: n.read ? 500 : 700,
                      lineHeight: 1.5,
                      marginTop: 4,
                    }}
                  >
                    {n.body}
                  </div>
                </div>
                <div
                  className="tabular-nums"
                  style={{
                    color: "#6C6B85",
                    fontSize: 12,
                    fontStyle: "italic",
                    fontFamily: '"Newsreader", Georgia, serif',
                    whiteSpace: "nowrap",
                  }}
                >
                  {relativeTime(n.created_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
